
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Zap, Crown, Award, ChevronRight } from 'lucide-react';
import { STATIC_BADGES } from '../../badges';
import * as Icons from 'lucide-react';

const LevelUpOverlay = ({ isOpen, onClose, addedMinutes, previousTotalMinutes, newBadges = [] }) => {
  const [phase, setPhase] = useState('xp'); // xp -> levelup -> badges -> end
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);
  const [canClose, setCanClose] = useState(false);
  
  // 計算ロジック: レベル = 時間 (例: 120分 = Lv.2)
  const currentTotalMinutes = previousTotalMinutes + addedMinutes;
  const prevLevel = Math.floor(previousTotalMinutes / 60);
  const currentLevel = Math.floor(currentTotalMinutes / 60);
  const isLevelUp = currentLevel > prevLevel;

  // 経験値バーの計算 (0-100%)
  const prevProgress = ((previousTotalMinutes % 60) / 60) * 100;
  const currentProgress = ((currentTotalMinutes % 60) / 60) * 100;

  useEffect(() => {
    if (isOpen) {
      setPhase('xp');
      setCurrentBadgeIndex(0);
      setCanClose(false);
      
      // シーケンス管理
      let timeout;
      if (isLevelUp) {
        timeout = setTimeout(() => {
          setPhase('levelup');
        }, 2500); 
      } else if (newBadges.length > 0) {
        timeout = setTimeout(() => {
          setPhase('badges');
        }, 2500);
      } else {
        // レベルアップもバッジもない場合は、XPアニメーション後に閉じるのを許可
        timeout = setTimeout(() => {
            setCanClose(true);
        }, 2500);
      }
      
      return () => clearTimeout(timeout);
    }
  }, [isOpen, isLevelUp, newBadges.length]);

  // レベルアップ表示後の遷移
  useEffect(() => {
    if (phase === 'levelup') {
      const timeout = setTimeout(() => {
        if (newBadges.length > 0) {
          setPhase('badges');
        } else {
          // 終了待ち状態 (バッジなしならここで終了可能に)
          setCanClose(true);
        }
      }, 3000); 
      return () => clearTimeout(timeout);
    }
  }, [phase, newBadges.length]);

  const handleNextBadge = (e) => {
    e.stopPropagation();
    if (currentBadgeIndex < newBadges.length - 1) {
      setCurrentBadgeIndex(prev => prev + 1);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  const currentNewBadge = newBadges[currentBadgeIndex];
  
  // バッジ情報の解決
  let badgeDisplay = {};
  if (currentNewBadge) {
      if (currentNewBadge.type === 'static') {
          badgeDisplay = {
              title: "NEW BADGE!",
              name: currentNewBadge.badge.name,
              desc: currentNewBadge.badge.description,
              icon: currentNewBadge.badge.icon,
              isEvolve: false
          };
      } else {
          // Evolving Badge
          badgeDisplay = {
              title: "LEVEL UP!",
              name: currentNewBadge.badgeName,
              desc: `Lv.${currentNewBadge.oldLevel} ➔ Lv.${currentNewBadge.newLevel}`,
              icon: Crown, // 進化時は共通アイコンまたはカテゴリに応じたものを
              isEvolve: true,
              info: currentNewBadge.info
          };
      }
  }

  const BadgeIconComponent = badgeDisplay.icon || Award;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/95 backdrop-blur-md cursor-pointer overflow-hidden"
        onClick={(e) => {
            if (phase === 'badges') handleNextBadge(e);
            else if (canClose) onClose();
        }}
      >
        {/* 背景エフェクト (共通) */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-tr from-indigo-500/20 via-purple-500/20 to-transparent rounded-full blur-[100px]" 
            />
        </div>

        {/* Phase 1 & 2: XP and Level Up */}
        {(phase === 'xp' || phase === 'levelup') && (
            <motion.div 
                className="w-full max-w-md text-center relative p-4"
                exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.3 } }}
            >
                {/* メインタイトル */}
                <motion.div
                    initial={{ scale: 0.5, opacity: 0, y: -50 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                    className="mb-8"
                >
                    <h2 className="text-4xl sm:text-5xl font-black italic tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-yellow-300 to-amber-600 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                    QUEST<br/>COMPLETE
                    </h2>
                    <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.3, type: "spring" }}
                        className="flex justify-center mt-2 gap-2"
                    >
                        {[...Array(3)].map((_, i) => (
                            <Star key={i} className="text-yellow-400 fill-yellow-400 drop-shadow-lg" size={24} />
                        ))}
                    </motion.div>
                </motion.div>

                {/* 獲得経験値 */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mb-8 flex justify-center items-baseline gap-2"
                >
                    <span className="text-slate-300 font-bold text-lg">獲得XP</span>
                    <span className="text-5xl font-black text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]">
                        +{addedMinutes}
                    </span>
                    <span className="text-indigo-300 font-bold">min</span>
                </motion.div>

                {/* ゲージとレベル表示 */}
                <div className="bg-slate-800/80 rounded-2xl p-6 border border-slate-700 shadow-2xl relative overflow-hidden">
                    <div className="flex justify-between items-end mb-2">
                        <div className="text-left">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Total Level</span>
                            <div className="text-3xl font-black text-white flex items-center gap-2">
                                Lv.
                                <span className="text-4xl">{prevLevel}</span>
                                {isLevelUp && (
                                    <motion.span 
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 1.5 }}
                                        className="text-emerald-400 text-2xl"
                                    >
                                        ➔ {currentLevel}
                                    </motion.span>
                                )}
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="text-xs font-bold text-slate-400">Total Time</span>
                            <div className="text-xl font-bold text-indigo-300">{Math.floor(currentTotalMinutes / 60)}h {currentTotalMinutes % 60}m</div>
                        </div>
                    </div>

                    {/* XP Bar */}
                    <div className="h-6 bg-slate-900 rounded-full overflow-hidden relative border border-slate-700">
                        <div 
                            className="absolute top-0 left-0 h-full bg-indigo-900/50" 
                            style={{ width: `${prevProgress}%` }} 
                        />
                        <motion.div 
                            initial={{ width: `${prevProgress}%` }}
                            animate={{ width: isLevelUp ? '100%' : `${currentProgress}%` }}
                            transition={{ duration: 1.0, delay: 0.8, ease: "circOut" }}
                            className="absolute top-0 left-0 h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-400"
                        />
                        <motion.div
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                            className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
                        />
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-500 mt-1 font-mono font-bold">
                        <span>0m</span>
                        <span>NEXT: 60m</span>
                    </div>

                    {/* Level Up Notification Overlay */}
                    <AnimatePresence>
                        {phase === 'levelup' && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
                                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                exit={{ opacity: 0, scale: 1.5 }}
                                transition={{ type: "spring", bounce: 0.5 }}
                                className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/90 backdrop-blur-sm"
                            >
                                <div className="text-center">
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-r from-yellow-500/20 to-purple-500/20 blur-xl rounded-full -z-10"
                                    />
                                    <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-2 drop-shadow-[0_0_15px_rgba(250,204,21,0.6)]" />
                                    <h3 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-b from-yellow-300 to-yellow-600 drop-shadow-sm">
                                        LEVEL UP!
                                    </h3>
                                    <p className="text-white font-bold text-xl mt-2 tracking-widest">
                                        Lv.{currentLevel} 到達
                                    </p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {canClose && (
                    <motion.p 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-8 text-slate-500 text-sm animate-pulse"
                    >
                        Click anywhere to close
                    </motion.p>
                )}
            </motion.div>
        )}

        {/* Phase 3: Badge Unlock / Evolve */}
        {phase === 'badges' && currentNewBadge && (
            <motion.div
                key="badge-phase"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full max-w-md text-center relative z-20 p-4"
            >
                <motion.div
                    key={currentBadgeIndex}
                    initial={{ scale: 0.5, opacity: 0, y: 50 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 1.5, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                    <h3 className="text-3xl font-black text-white mb-6 tracking-widest drop-shadow-md">
                        {badgeDisplay.title}
                    </h3>
                    
                    <div className="relative w-48 h-48 mx-auto mb-8">
                        {/* God rays effect */}
                        <motion.div 
                            animate={{ rotate: 360 }}
                            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                            className="absolute inset-0 bg-gradient-to-r from-yellow-500/0 via-yellow-500/20 to-yellow-500/0 rounded-full blur-md"
                            style={{ clipPath: 'polygon(50% 50%, 0 0, 100% 0)' }}
                        />
                        
                        {/* Badge Icon */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className={`w-32 h-32 rounded-full flex items-center justify-center shadow-[0_0_50px_rgba(251,191,36,0.6)] border-4 border-white bg-gradient-to-br ${badgeDisplay.isEvolve ? 'from-indigo-100 to-indigo-300' : 'from-amber-100 to-amber-300'}`}>
                                <BadgeIconComponent 
                                    size={64} 
                                    className={badgeDisplay.isEvolve ? 'text-indigo-600' : 'text-amber-600'} 
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-slate-800/80 backdrop-blur-sm p-6 rounded-2xl border border-slate-700 shadow-xl">
                        <h4 className={`text-2xl font-bold mb-2 ${badgeDisplay.isEvolve ? 'text-indigo-400' : 'text-amber-400'}`}>
                            {badgeDisplay.name}
                        </h4>
                        
                        {badgeDisplay.isEvolve ? (
                            <div className="text-white font-bold text-xl flex items-center justify-center gap-2">
                                <span className="text-slate-400 text-lg">Lv.{currentNewBadge.oldLevel}</span>
                                <ChevronRight className="text-indigo-500" />
                                <span className="text-yellow-400 text-3xl">Lv.{currentNewBadge.newLevel}</span>
                            </div>
                        ) : (
                            <p className="text-slate-300">{badgeDisplay.desc}</p>
                        )}
                        
                        {/* 進化時の追加情報 (称号ラベルなど) */}
                        {badgeDisplay.isEvolve && currentNewBadge.info && (
                            <p className="mt-2 text-sm text-indigo-300 font-bold uppercase tracking-widest">
                                [{currentNewBadge.info.label}]
                            </p>
                        )}
                    </div>

                    <p className="mt-8 text-slate-400 text-sm animate-pulse">
                        {currentBadgeIndex < newBadges.length - 1 ? 'Click for next' : 'Click to close'}
                    </p>
                </motion.div>
            </motion.div>
        )}

      </motion.div>
    </AnimatePresence>
  );
};

export default LevelUpOverlay;
    